import {
  ArrowUpRight,
  CheckCircle2,
  ChevronDown,
  CreditCard,
  Download,
  FileText,
  LogIn,
  LogOut,
  LoaderCircle,
  Mail,
  Phone,
  RefreshCw,
  Sun,
  X,
  UserRound,
} from "lucide-react";
import type { User } from "firebase/auth";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  DEFAULT_PHONE_COUNTRY_CODE,
  KAJIKAJY_PLANS,
  KAJIKAJY_PHONE_COUNTRIES,
  PAPI_PAYMENT_ORIGIN,
  WEB_I18N,
  WEB_LOCALES,
  WEB_SOCIAL_ICONS,
  WEB_SOCIAL_LINKS,
  WEB_SITE_PATHS,
  applyKajikajyTheme,
  clearPendingPayment,
  formatLocalPhoneForCountry,
  getInitialKajikajyTheme,
  getInitialWebLocale,
  getPhoneCountry,
  isAllowedKajikajyPaymentOrigin,
  isKajikajyPaymentReturnMessage,
  isPapiPaymentStatusMessage,
  mapInvoiceStatusToPaymentPhase,
  navigateToPaymentCheckout,
  onlyPhoneDigits,
  openCenteredPaymentPopup,
  parsePaymentReturnQuery,
  persistKajikajyTheme,
  persistPendingPayment,
  persistWebLocale,
  readPendingPayment,
  readSharedKajikajyTheme,
  readSharedWebLocale,
  removePaymentReturnQuery,
  resolveWebLocale,
  type InvoiceStatus,
  type KajikajyPlanId,
  type KajikajyThemeMode,
  type PaymentFlowPhase,
  type PendingPayment,
  type SubscriptionStatus,
  type WebLocale,
} from "@kajikajy/shared";
import {
  activateLegacyPhonePin,
  createAccountPaymentSession,
  signInAccountWithPhonePin,
  getAccountDashboard,
  getPaymentStatus,
  observeAccountUser,
  signInAccount,
  signOutAccount,
  type AccountCustomerView,
  type AccountDashboardView,
  type AccountInvoiceView,
  type CreateAccountPaymentSessionResult,
  type SubscriptionView,
} from "./lib/subscriptionApi";
import { getWebAppAccessUntil } from "./lib/webAppAccess";
import { beginWebAppSession, isWebSessionAccountCurrent } from "./lib/webAppSession";
import { startWebSessionHandoff, WebSessionHandoffError, type WebSessionHandoff } from "./lib/webSessionHandoff";

const invoiceIssuer = {
  name: "KAJIKAJY",
  nif: "6019768936",
  rcs: "2026B000761",
  stat: "62011 11 2026 0 10779",
  address: "Lot K7 197 Bis Mamory Ivato",
};

type SigninMode = "phone" | "email";
type AccountInvoiceAction = "renewal" | "upgrade";
type PaymentVerificationMode = "continuous" | "once";

type AccountCopy = {
  title: string;
  lead: string;
  signinTitle: string;
  signinText: string;
  phoneMode: string;
  emailMode: string;
  phoneNumberLabel: string;
  phoneCountryLabel: string;
  phoneCountrySearch: string;
  phoneCountryNoResults: string;
  phonePlaceholder: string;
  pinLabel: string;
  pinPlaceholder: string;
  forgotPinPrompt: string;
  forgotPinAction: string;
  emailLabel: string;
  emailPlaceholder: string;
  passwordLabel: string;
  passwordPlaceholder: string;
  signinButton: string;
  signOut: string;
  signedInAs: string;
  openKajikajy: string;
  webAccessError: string;
  webAccessPopupBlocked: string;
  loading: string;
  firebaseMissing: string;
  signinError: string;
  phoneValidationError: string;
  pinValidationError: string;
  phoneSigninError: string;
  pinLockedError: string;
  reconnectRequired: string;
  noPaidAccountText: string;
  accountLookupHelpTitle: string;
  accountLookupHelpText: string;
  accountLookupHelpButton: string;
  subscriptionError: string;
  activePlanLabel: string;
  statusLabel: string;
  expiresLabel: string;
  graceLabel: string;
  activatedLabel: string;
  invoiceLabel: string;
  paymentReferenceLabel: string;
  phoneLabel: string;
  subscriptionTitle: string;
  actionsTitle: string;
  actionsRenewText: string;
  actionsUpgradeText: string;
  accountInvoicePreparing: string;
  accountInvoiceReadyTitle: string;
  accountInvoiceRenewalReadyText: string;
  accountInvoiceUpgradeReadyText: string;
  accountInvoiceReferenceLabel: string;
  accountInvoiceAmountLabel: string;
  accountInvoiceError: string;
  paymentDialogTitle: string;
  paymentDialogText: string;
  paymentPreparingText: string;
  paymentProviderLabel: string;
  paymentUnavailableText: string;
  payButton: string;
  retryButton: string;
  closeButton: string;
  renewButton: string;
  upgradeButton: string;
  invoicesTitle: string;
  invoicesEmptyTitle: string;
  invoicesEmptyText: string;
  invoiceDownloadLabel: string;
  invoiceDocumentTitle: string;
  invoiceNumberLabel: string;
  invoiceDateLabel: string;
  invoiceAmountLabel: string;
  accountInfoTitle: string;
  businessLabel: string;
  emailInfoLabel: string;
  emptyValue: string;
  statusGridLabel: string;
  statusTitle: string;
  planTitle: string;
  accessTitle: string;
  statuses: Record<SubscriptionStatus | "none", string>;
  invoiceStatuses: Record<InvoiceStatus, string>;
};

const ACCOUNT_COPY: Record<WebLocale, AccountCopy> = {
  fr: {
    title: "Mon compte",
    lead: "Retrouvez votre abonnement, vos factures et les actions utiles pour continuer avec Kajikajy.",
    signinTitle: "Connexion",
    signinText:
      "Utilisez le numéro de téléphone ou l'adresse mail liée au compte Kajikajy.",
    phoneMode: "Téléphone",
    emailMode: "Email",
    phoneNumberLabel: "Numéro du compte",
    phoneCountryLabel: "Indicatif pays",
    phoneCountrySearch: "Rechercher un pays ou un code",
    phoneCountryNoResults: "Aucun pays trouvé.",
    phonePlaceholder: "38 00 000 00",
    pinLabel: "Code PIN",
    pinPlaceholder: "4 chiffres",
    forgotPinPrompt: "Code PIN oubli\u00e9 ?",
    forgotPinAction: "Ouvrez Aide et support dans l\u2019app.",
    emailLabel: "Email du compte",
    emailPlaceholder: "nom@exemple.com",
    passwordLabel: "Mot de passe",
    passwordPlaceholder: "Mot de passe",
    signinButton: "Se connecter",
    signOut: "Se déconnecter",
    signedInAs: "Compte connecté",
    openKajikajy: "Ouvrir Kajikajy web",
    webAccessError: "Impossible d’ouvrir Kajikajy web pour l’instant. Réessayez.",
    webAccessPopupBlocked: "Autorisez l’ouverture d’une nouvelle fenêtre, puis réessayez.",
    loading: "Chargement du compte",
    firebaseMissing: "La configuration Firebase web est absente.",
    signinError: "Connexion impossible. Vérifiez les informations saisies.",
    phoneValidationError: "Indiquez un numéro valide.",
    pinValidationError: "Saisissez votre code PIN \u00e0 4 chiffres.",
    phoneSigninError: "Le num\u00e9ro ou le code PIN est incorrect.",
    pinLockedError: "Trop de tentatives. R\u00e9essayez plus tard.",
    reconnectRequired: "Reconnectez-vous avec votre code PIN ou par email.",
    noPaidAccountText:
      "Aucun compte Kajikajy payant n'est rattaché à ces informations.",
    accountLookupHelpTitle: "Pas encore de compte Kajikajy ?",
    accountLookupHelpText:
      "Créez votre compte depuis la page Souscrire. Après paiement, l'abonnement sera rattaché au numéro ou à l'email indiqué.",
    accountLookupHelpButton: "Créer un compte",
    subscriptionError: "Impossible de charger le compte.",
    activePlanLabel: "Plan",
    statusLabel: "Statut",
    expiresLabel: "Expiration",
    graceLabel: "Grâce jusqu'au",
    activatedLabel: "Activé le",
    invoiceLabel: "Dernière facture",
    paymentReferenceLabel: "Référence paiement",
    phoneLabel: "Téléphone",
    subscriptionTitle: "Abonnement",
    actionsTitle: "Renouvellement",
    actionsRenewText: "Prolongez l'accès au plan actuel.",
    actionsUpgradeText:
      "Renouvelez ou passez à Excellence pour activer les avantages du plan supérieur",
    accountInvoicePreparing: "Préparation du paiement",
    accountInvoiceReadyTitle: "Paiement prêt",
    accountInvoiceRenewalReadyText: "Renouvellement du plan actuel",
    accountInvoiceUpgradeReadyText: "Passage à Excellence",
    accountInvoiceReferenceLabel: "Référence",
    accountInvoiceAmountLabel: "Montant",
    accountInvoiceError: "Le paiement n'a pas pu être préparé.",
    paymentDialogTitle: "Paiement",
    paymentDialogText: "Vérifiez le montant puis lancez le paiement.",
    paymentPreparingText:
      "Connexion sécurisée à Papi. Cette étape peut prendre quelques secondes.",
    paymentProviderLabel: "Prestataire",
    paymentUnavailableText:
      "Le paiement n'est pas disponible dans cet environnement.",
    payButton: "Payer",
    retryButton: "Réessayer",
    closeButton: "Fermer",
    renewButton: "Renouveler",
    upgradeButton: "Passer à Excellence",
    invoicesTitle: "Factures",
    invoicesEmptyTitle: "Aucune facture",
    invoicesEmptyText:
      "Les factures d'abonnement apparaîtront ici après paiement.",
    invoiceDownloadLabel: "Télécharger",
    invoiceDocumentTitle: "Facture d'abonnement Kajikajy",
    invoiceNumberLabel: "Numéro de facture",
    invoiceDateLabel: "Date",
    invoiceAmountLabel: "Montant",
    accountInfoTitle: "Informations du compte",
    businessLabel: "Activité",
    emailInfoLabel: "Email",
    emptyValue: "Non renseigné",
    statusGridLabel: "Détails abonnement",
    statusTitle: "État",
    planTitle: "Offre",
    accessTitle: "Accès",
    statuses: {
      active: "Actif",
      grace: "Période de grâce",
      past_due: "Paiement en retard",
      expired: "Expiré",
      cancelled: "Annulé",
      none: "Non actif",
    },
    invoiceStatuses: {
      draft: "Brouillon",
      pending: "En attente",
      paid: "Payée",
      cancelled: "Annulée",
      expired: "Expirée",
      failed: "Échouée",
    },
  },
  mg: {
    title: "Ny kaontiko",
    lead: "Jereo eto ny tolotra ampiasainao, ny faktioranao ary izay azonao atao hanohizana ny fampiasana an’i Kajikajy.",
    signinTitle: "Fidirana amin’ny kaonty",
    signinText:
      "Ampiasao ny laharana finday na ny adiresy email mifandray amin’ny kaonty Kajikajy-nao.",
    phoneMode: "Laharana finday",
    emailMode: "Email",
    phoneNumberLabel: "Laharana finday mifandray amin’ny kaonty",
    phoneCountryLabel: "Kaodin’ny firenena",
    phoneCountrySearch: "Karohy ny firenena na ny kaody",
    phoneCountryNoResults: "Tsy nahitana firenena.",
    phonePlaceholder: "38 00 000 00",
    pinLabel: "Kaody PIN",
    pinPlaceholder: "Isa 4",
    forgotPinPrompt: "Hadinonao ny kaody PIN?",
    forgotPinAction: "Sokafy ao amin\u2019ny app ny Fanampiana sy Support.",
    emailLabel: "Email mifandray amin’ny kaonty",
    emailPlaceholder: "anarana@ohatra.com",
    passwordLabel: "Teny miafina",
    passwordPlaceholder: "Teny miafina",
    signinButton: "Hiditra",
    signOut: "Hivoaka",
    signedInAs: "Kaonty ampiasainao",
    openKajikajy: "Sokafy Kajikajy web",
    webAccessError: "Tsy afaka manokatra an’i Kajikajy web amin’izao fotoana izao. Andramo indray.",
    webAccessPopupBlocked: "Avelao hisokatra ny varavarankely vaovao, dia andramo indray.",
    loading: "Eo am-panokafana ny kaonty…",
    firebaseMissing: "Tsy mandeha amin’izao fotoana izao ny rafitra fidirana.",
    signinError:
      "Tsy afaka miditra ianao. Hamarino ny mombamomba nampidirinao.",
    phoneValidationError: "Ampidiro ny laharana finday marina.",
    pinValidationError: "Ampidiro ny kaody PIN misy isa 4.",
    phoneSigninError: "Tsy mifanaraka ny laharana finday sy ny kaody PIN.",
    pinLockedError: "Be loatra ny fanandramana. Andramo indray any aoriana.",
    reconnectRequired:
      "Midira indray amin\u2019ny kaody PIN-nao na amin\u2019ny email.",
    noPaidAccountText:
      "Tsy nahitana kaonty Kajikajy misy tolotra voaloa mifandray amin’ireo mombamomba ireo.",
    accountLookupHelpTitle: "Mbola tsy manana kaonty Kajikajy?",
    accountLookupHelpText:
      "Mamoròna kaonty ao amin’ny pejy Hisoratra anarana. Rehefa voamarina ny fandoavana dia hampifandraisina amin’ny laharana finday na email nampidirinao ny tolotra nosafidinao.",
    accountLookupHelpButton: "Hamorona kaonty",
    subscriptionError:
      "Tsy afaka nampiseho ny kaonty. Andramo indray afaka fotoana fohy.",
    activePlanLabel: "Tolotra",
    statusLabel: "Toetry ny tolotra",
    expiresLabel: "Hifarana amin’ny",
    graceLabel: "Fe-potoana fanampiny hatramin’ny",
    activatedLabel: "Nanomboka tamin’ny",
    invoiceLabel: "Faktiora farany",
    paymentReferenceLabel: "Laharana famantarana ny fandoavana",
    phoneLabel: "Laharana finday",
    subscriptionTitle: "Tolotra ampiasainao",
    actionsTitle: "Fanavaozana ny tolotra",
    actionsRenewText:
      "Havaozy ny tolotra ampiasainao mba hanohizana ny fampiasana an’i Kajikajy.",
    actionsUpgradeText:
      "Azonao havaozina ny Essor, na afindra amin’ny Excellence ny kaontinao raha mila mampiasa an’i Kajikajy amin’ny fitaovana maromaro ianao.",
    accountInvoicePreparing: "Eo am-panomanana ny fandoavana…",
    accountInvoiceReadyTitle: "Afaka mandoa ianao izao",
    accountInvoiceRenewalReadyText: "Fanavaozana ny tolotra ampiasainao",
    accountInvoiceUpgradeReadyText: "Fifindrana amin’ny Excellence",
    accountInvoiceReferenceLabel: "Laharana famantarana ny fandoavana",
    accountInvoiceAmountLabel: "Vola aloa",
    accountInvoiceError:
      "Tsy voaomana ny fandoavana. Andramo indray afaka fotoana fohy.",
    paymentDialogTitle: "Fandoavana",
    paymentDialogText:
      "Hamarino ny vola tokony haloanao, dia tohizo amin’ny Mobile Money ny fandoavana.",
    paymentPreparingText:
      "Eo am-panomanana ny fandoavana amin’ny fomba azo antoka. Mety haharitra segondra vitsy izany.",
    paymentProviderLabel: "Mpikirakira ny fandoavana",
    paymentUnavailableText:
      "Tsy afaka mandoa eto ianao amin’izao fotoana izao.",
    payButton: "Handoa izao",
    retryButton: "Andramo indray",
    closeButton: "Akatona",
    renewButton: "Hanavao ny tolotra",
    upgradeButton: "Hifindra amin’ny Excellence",
    invoicesTitle: "Faktiora",
    invoicesEmptyTitle: "Tsy mbola misy faktiora",
    invoicesEmptyText:
      "Hiseho eto ny faktioran’ny tolotra rehefa voamarina ny fandoavana.",
    invoiceDownloadLabel: "Sintomy ny faktiora",
    invoiceDocumentTitle: "Faktioran’ny tolotra Kajikajy",
    invoiceNumberLabel: "Laharan’ny faktiora",
    invoiceDateLabel: "Daty",
    invoiceAmountLabel: "Vola aloa",
    accountInfoTitle: "Mombamomba ny kaonty",
    businessLabel: "Anaran’ny asanao",
    emailInfoLabel: "Email",
    emptyValue: "Tsy nampidirina",
    statusGridLabel: "Antsipirian’ny tolotra ampiasaina",
    statusTitle: "Toetry ny tolotra",
    planTitle: "Tolotra",
    accessTitle: "Fahafahana mampiasa",
    statuses: {
      active: "Mandeha",
      grace: "Fe-potoana fanampiny",
      past_due: "Mbola tsy voaloa",
      expired: "Tapitra",
      cancelled: "Nofoanana",
      none: "Tsy misy tolotra mandeha",
    },
    invoiceStatuses: {
      draft: "Eo am-panomanana",
      pending: "Miandry fandoavana",
      paid: "Voaloa",
      cancelled: "Nofoanana",
      expired: "Tapitra ny fe-potoana",
      failed: "Tsy tontosa",
    },
  },
  en: {
    title: "My account",
    lead: "Find your subscription, invoices and available actions to keep using Kajikajy.",
    signinTitle: "Sign in",
    signinText:
      "Use the phone number or email address attached to the Kajikajy account.",
    phoneMode: "Phone",
    emailMode: "Email",
    phoneNumberLabel: "Account phone",
    phoneCountryLabel: "Country code",
    phoneCountrySearch: "Search country or code",
    phoneCountryNoResults: "No country found.",
    phonePlaceholder: "38 00 000 00",
    pinLabel: "PIN",
    pinPlaceholder: "4 digits",
    forgotPinPrompt: "Forgot your PIN?",
    forgotPinAction: "Open Help and support in the app.",
    emailLabel: "Account email",
    emailPlaceholder: "name@example.com",
    passwordLabel: "Password",
    passwordPlaceholder: "Password",
    signinButton: "Sign in",
    signOut: "Sign out",
    signedInAs: "Signed in account",
    openKajikajy: "Open Kajikajy web",
    webAccessError: "Kajikajy web could not be opened right now. Please try again.",
    webAccessPopupBlocked: "Allow a new window to open, then try again.",
    loading: "Loading account",
    firebaseMissing: "Firebase web configuration is missing.",
    signinError: "Sign-in failed. Check the details entered.",
    phoneValidationError: "Enter a valid phone number.",
    pinValidationError: "Enter your 4-digit PIN.",
    phoneSigninError: "The phone number or PIN is incorrect.",
    pinLockedError: "Too many attempts. Try again later.",
    reconnectRequired: "Sign in again with your PIN or email.",
    noPaidAccountText: "No paid Kajikajy account is attached to these details.",
    accountLookupHelpTitle: "No Kajikajy account yet?",
    accountLookupHelpText:
      "Create an account from the Subscribe page. After payment, the subscription will be attached to the phone number or email entered.",
    accountLookupHelpButton: "Create an account",
    subscriptionError: "The account could not be loaded.",
    activePlanLabel: "Plan",
    statusLabel: "Status",
    expiresLabel: "Expiration",
    graceLabel: "Grace until",
    activatedLabel: "Activated on",
    invoiceLabel: "Latest invoice",
    paymentReferenceLabel: "Payment reference",
    phoneLabel: "Phone",
    subscriptionTitle: "Subscription",
    actionsTitle: "Renewal",
    actionsRenewText: "Extend access to the current plan.",
    actionsUpgradeText:
      "Move to Excellence to activate the higher plan benefits.",
    accountInvoicePreparing: "Preparing payment",
    accountInvoiceReadyTitle: "Payment ready",
    accountInvoiceRenewalReadyText: "Current plan renewal",
    accountInvoiceUpgradeReadyText: "Move to Excellence",
    accountInvoiceReferenceLabel: "Reference",
    accountInvoiceAmountLabel: "Amount",
    accountInvoiceError: "The payment could not be prepared.",
    paymentDialogTitle: "Payment",
    paymentDialogText: "Check the amount, then continue to payment.",
    paymentPreparingText:
      "Connecting securely to Papi. This may take a few seconds.",
    paymentProviderLabel: "Provider",
    paymentUnavailableText: "Payment is not available in this environment.",
    payButton: "Pay",
    retryButton: "Try again",
    closeButton: "Close",
    renewButton: "Renew",
    upgradeButton: "Move to Excellence",
    invoicesTitle: "Invoices",
    invoicesEmptyTitle: "No invoice",
    invoicesEmptyText: "Subscription invoices will appear here after payment.",
    invoiceDownloadLabel: "Download",
    invoiceDocumentTitle: "Kajikajy subscription invoice",
    invoiceNumberLabel: "Invoice number",
    invoiceDateLabel: "Date",
    invoiceAmountLabel: "Amount",
    accountInfoTitle: "Account details",
    businessLabel: "Business",
    emailInfoLabel: "Email",
    emptyValue: "Not provided",
    statusGridLabel: "Subscription details",
    statusTitle: "State",
    planTitle: "Offer",
    accessTitle: "Access",
    statuses: {
      active: "Active",
      grace: "Grace period",
      past_due: "Payment overdue",
      expired: "Expired",
      cancelled: "Cancelled",
      none: "Inactive",
    },
    invoiceStatuses: {
      draft: "Draft",
      pending: "Pending",
      paid: "Paid",
      cancelled: "Cancelled",
      expired: "Expired",
      failed: "Failed",
    },
  },
};

type LegacyPinActivationCopy = {
  eyebrow: string;
  title: string;
  lead: string;
  formTitle: string;
  formText: string;
  activationCodeLabel: string;
  activationCodePlaceholder: string;
  createPinLabel: string;
  confirmPinLabel: string;
  activateButton: string;
  activatingButton: string;
  activationCodeRequired: string;
  pinRequired: string;
  pinMismatch: string;
  activationError: string;
  successTitle: string;
  successText: string;
};

const LEGACY_PIN_ACTIVATION_COPY: Record<WebLocale, LegacyPinActivationCopy> = {
  fr: {
    eyebrow: "S\u00e9curit\u00e9 du compte",
    title: "Cr\u00e9ez votre code PIN",
    lead:
      "S\u00e9curisez l\u2019acc\u00e8s \u00e0 votre compte Kajikajy avec un code PIN \u00e0 4 chiffres.",
    formTitle: "Activer le code PIN",
    formText:
      "Saisissez le code d\u2019activation qui vous a \u00e9t\u00e9 remis, puis choisissez votre code PIN.",
    activationCodeLabel: "Code d\u2019activation",
    activationCodePlaceholder: "Saisissez le code",
    createPinLabel: "Cr\u00e9er un code PIN",
    confirmPinLabel: "Confirmer le code",
    activateButton: "Activer le code PIN",
    activatingButton: "Activation en cours",
    activationCodeRequired: "Saisissez votre code d\u2019activation.",
    pinRequired: "Cr\u00e9ez un code PIN \u00e0 4 chiffres.",
    pinMismatch: "Les deux codes PIN ne correspondent pas.",
    activationError:
      "Activation impossible. V\u00e9rifiez les informations saisies.",
    successTitle: "Code PIN activ\u00e9",
    successText:
      "Votre compte est prot\u00e9g\u00e9. Vous pouvez fermer cette page et vous connecter \u00e0 Mon compte.",
  },
  mg: {
    eyebrow: "Fiarovana ny kaonty",
    title: "Mamor\u00f2na kaody PIN",
    lead:
      "Mamor\u00f2na kaody PIN misy isa 4 hiarovana ny kaonty Kajikajy-nao.",
    formTitle: "Famoronana ny kaody PIN",
    formText:
      "Ampidiro ilay kaody fanamarinana nomena anao, dia mamor\u00f2na kaody PIN.",
    activationCodeLabel: "Kaody fanamarinana",
    activationCodePlaceholder: "Ampidiro ilay kaody",
    createPinLabel: "Mamor\u00f2na kaody PIN",
    confirmPinLabel: "Avereno ilay kaody",
    activateButton: "Hamorona kaody PIN",
    activatingButton: "Eo am-pamoronana",
    activationCodeRequired: "Ampidiro ilay kaody fanamarinana.",
    pinRequired: "Mamor\u00f2na kaody PIN misy isa 4.",
    pinMismatch: "Tsy mitovy ireo kaody PIN nampidirinao.",
    activationError:
      "Tsy voaforona ny kaody PIN. Hamarino izay nampidirinao.",
    successTitle: "Voaforona ny kaody PIN",
    successText:
      "Voaaro izao ny kaontinao. Akatona ity pejy ity, dia midira ao amin\u2019ny kaontinao.",
  },
  en: {
    eyebrow: "Account security",
    title: "Create your PIN",
    lead: "Protect access to your Kajikajy account with a 4-digit PIN.",
    formTitle: "Activate your PIN",
    formText:
      "Enter the activation code you received, then choose your PIN.",
    activationCodeLabel: "Activation code",
    activationCodePlaceholder: "Enter the code",
    createPinLabel: "Create a PIN",
    confirmPinLabel: "Confirm the PIN",
    activateButton: "Activate the PIN",
    activatingButton: "Activating",
    activationCodeRequired: "Enter your activation code.",
    pinRequired: "Create a 4-digit PIN.",
    pinMismatch: "The two PINs do not match.",
    activationError: "Activation failed. Check the information entered.",
    successTitle: "PIN activated",
    successText:
      "Your account is protected. You can close this page and sign in to My account.",
  },
};

function getBrowserLocale(): WebLocale {
  if (typeof navigator === "undefined") {
    return "fr";
  }
  return resolveWebLocale(navigator.language.slice(0, 2), "fr");
}

function useThemeMode() {
  const [theme, setTheme] = useState<KajikajyThemeMode>(
    getInitialKajikajyTheme,
  );

  useEffect(() => {
    applyKajikajyTheme(theme);
    persistKajikajyTheme(theme);
  }, [theme]);

  useEffect(() => {
    const syncSharedTheme = () => {
      const sharedTheme = readSharedKajikajyTheme();
      if (sharedTheme) {
        setTheme((current) =>
          current === sharedTheme ? current : sharedTheme,
        );
      }
    };
    const syncVisibleTheme = () => {
      if (!document.hidden) {
        syncSharedTheme();
      }
    };

    window.addEventListener("focus", syncSharedTheme);
    window.addEventListener("pageshow", syncSharedTheme);
    document.addEventListener("visibilitychange", syncVisibleTheme);
    return () => {
      window.removeEventListener("focus", syncSharedTheme);
      window.removeEventListener("pageshow", syncSharedTheme);
      document.removeEventListener("visibilitychange", syncVisibleTheme);
    };
  }, []);

  return {
    theme,
    toggleTheme: () =>
      setTheme((current) => (current === "dark" ? "light" : "dark")),
  };
}

function getInitialLocale(): WebLocale {
  return getInitialWebLocale(getBrowserLocale());
}

function useWebLocale() {
  const [locale, setLocale] = useState<WebLocale>(getInitialLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
    persistWebLocale(locale);
  }, [locale]);

  useEffect(() => {
    const syncSharedLocale = () => {
      const sharedLocale = readSharedWebLocale();
      if (sharedLocale) {
        setLocale((current) =>
          current === sharedLocale ? current : sharedLocale,
        );
      }
    };
    const syncVisibleLocale = () => {
      if (!document.hidden) {
        syncSharedLocale();
      }
    };

    window.addEventListener("focus", syncSharedLocale);
    window.addEventListener("pageshow", syncSharedLocale);
    document.addEventListener("visibilitychange", syncVisibleLocale);
    return () => {
      window.removeEventListener("focus", syncSharedLocale);
      window.removeEventListener("pageshow", syncSharedLocale);
      document.removeEventListener("visibilitychange", syncVisibleLocale);
    };
  }, []);

  return { locale, setLocale };
}

function ThemeMoonIcon() {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3a6 6 0 0 0 9 6 9 9 0 1 1-9-6Z" />
    </svg>
  );
}

type LanguageMenuProps = {
  label: string;
  locale: WebLocale;
  onLocaleChange: (locale: WebLocale) => void;
};

function LanguageMenu({ label, locale, onLocaleChange }: LanguageMenuProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const closeOnOutside = (event: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("pointerdown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div className="kj-language-menu" ref={menuRef}>
      <button
        className="kj-language-trigger"
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-locale-trigger
        onClick={() => setOpen((current) => !current)}
      >
        <span data-locale-current>{WEB_I18N[locale].languageCode}</span>
        <ChevronDown aria-hidden size={15} />
      </button>
      {open ? (
        <div className="kj-language-popover" role="listbox" aria-label={label}>
          {WEB_LOCALES.map((option) => (
            <button
              className="kj-language-option"
              key={option}
              type="button"
              role="option"
              aria-selected={option === locale}
              data-locale-option={option}
              onClick={() => {
                onLocaleChange(option);
                setOpen(false);
              }}
            >
              {WEB_I18N[option].languageName}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function isValidLocalPhone(countryCode: string, value: string): boolean {
  const digits = onlyPhoneDigits(value);
  return countryCode === DEFAULT_PHONE_COUNTRY_CODE
    ? digits.length === 9
    : digits.length >= 6;
}

function buildE164PhoneNumber(
  countryCode: string,
  localNumber: string,
): string {
  return `${countryCode}${onlyPhoneDigits(localNumber)}`;
}

function formatStoredPhoneNumber(
  value: string | null | undefined,
): string | null {
  if (!value) {
    return null;
  }

  const compact = value.replace(/[^\d+]/g, "");
  if (compact.startsWith(DEFAULT_PHONE_COUNTRY_CODE)) {
    const localDigits = compact
      .slice(DEFAULT_PHONE_COUNTRY_CODE.length)
      .slice(0, 9);
    if (localDigits.length === 9) {
      return `${DEFAULT_PHONE_COUNTRY_CODE}${formatLocalPhoneForCountry(localDigits, DEFAULT_PHONE_COUNTRY_CODE)}`;
    }
  }

  return value;
}

type PhoneCountryFieldProps = {
  label: string;
  countryLabel: string;
  searchPlaceholder: string;
  noResults: string;
  placeholder: string;
  countryCode: string;
  localNumber: string;
  onCountryCodeChange: (countryCode: string) => void;
  onLocalNumberChange: (localNumber: string) => void;
};

function PhoneCountryField({
  label,
  countryLabel,
  searchPlaceholder,
  noResults,
  placeholder,
  countryCode,
  localNumber,
  onCountryCodeChange,
  onLocalNumberChange,
}: PhoneCountryFieldProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const fieldRef = useRef<HTMLDivElement>(null);
  const selectedCountry = getPhoneCountry(countryCode);

  const filteredCountries = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return KAJIKAJY_PHONE_COUNTRIES;
    }

    return KAJIKAJY_PHONE_COUNTRIES.filter(
      (country) =>
        country.name.toLowerCase().includes(normalizedQuery) ||
        country.code.includes(normalizedQuery),
    );
  }, [query]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const closeOnOutside = (event: PointerEvent) => {
      if (
        fieldRef.current &&
        !fieldRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("pointerdown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div className="field phone-field" ref={fieldRef}>
      <span>{label}</span>
      <div className="phone-control">
        <button
          className="phone-country-trigger"
          type="button"
          aria-label={countryLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
        >
          <span className="phone-country-flag" aria-hidden>
            {selectedCountry.flag}
          </span>
          <span>{selectedCountry.code}</span>
          <ChevronDown aria-hidden size={15} />
        </button>
        <input
          className="phone-number-input"
          autoComplete="tel-national"
          inputMode="tel"
          placeholder={placeholder}
          value={localNumber}
          onChange={(event) => {
            onLocalNumberChange(
              formatLocalPhoneForCountry(event.target.value, countryCode),
            );
          }}
        />
        {open ? (
          <div
            className="phone-country-popover"
            role="listbox"
            aria-label={countryLabel}
          >
            <input
              className="phone-country-search"
              aria-label={searchPlaceholder}
              autoComplete="off"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            <div className="phone-country-list">
              {filteredCountries.length > 0 ? (
                filteredCountries.map((country) => (
                  <button
                    className="phone-country-option"
                    key={country.code}
                    type="button"
                    role="option"
                    aria-selected={country.code === countryCode}
                    onClick={() => {
                      onCountryCodeChange(country.code);
                      onLocalNumberChange("");
                      setOpen(false);
                      setQuery("");
                    }}
                  >
                    <span className="phone-country-flag" aria-hidden>
                      {country.flag}
                    </span>
                    <span className="phone-country-name">{country.name}</span>
                    <span className="phone-country-code">{country.code}</span>
                  </button>
                ))
              ) : (
                <span className="phone-country-empty">{noResults}</span>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function formatDate(
  value: string | null,
  locale: WebLocale,
  fallback: string,
): string {
  if (!value) {
    return fallback;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  const dateLocale =
    locale === "mg" ? "mg-MG" : locale === "en" ? "en" : "fr-MG";
  return new Intl.DateTimeFormat(dateLocale, {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

function formatMoney(amount: number): string {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) {
    return "-";
  }
  return `${new Intl.NumberFormat("fr-MG").format(numericAmount)} Ar`;
}

function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function getPlanLabel(planId: KajikajyPlanId | null, fallback: string): string {
  return planId ? KAJIKAJY_PLANS[planId].label : fallback;
}

function getStatusLabel(
  status: SubscriptionStatus | null,
  copy: AccountCopy,
): string {
  return copy.statuses[status ?? "none"];
}

function isLocalHostname() {
  if (typeof window === "undefined") {
    return false;
  }

  return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

function getSiteHref(path = "/") {
  return isLocalHostname()
    ? `http://localhost:4321${path}`
    : `https://kajikajy.com${path}`;
}

function getSubscribeHomeHref() {
  return isLocalHostname()
    ? "http://localhost:5174/"
    : "https://souscrire.kajikajy.com/";
}

function isPermissionDeniedError(error: unknown) {
  const code =
    typeof error === "object" && error && "code" in error
      ? String((error as { code?: unknown }).code ?? "").toLowerCase()
      : "";

  return (
    code === "permission-denied" ||
    code === "functions/permission-denied"
  );
}

function getAccountErrorMessage(error: unknown, copy: AccountCopy) {
  const code =
    typeof error === "object" && error && "code" in error
      ? String((error as { code?: unknown }).code ?? "")
      : "";
  const message = error instanceof Error ? error.message : String(error ?? "");
  const normalized = `${code} ${message}`.toLowerCase();

  if (
    normalized.includes("internal") ||
    normalized.includes("not-found") ||
    normalized.includes("user-not-found") ||
    normalized.includes("invalid-credential")
  ) {
    return copy.noPaidAccountText;
  }

  if (normalized.includes("firebase web config")) {
    return copy.firebaseMissing;
  }

  return copy.subscriptionError;
}

function getPhonePinErrorMessage(error: unknown, copy: AccountCopy) {
  const code =
    typeof error === "object" && error && "code" in error
      ? String((error as { code?: unknown }).code ?? "")
      : "";
  const message = error instanceof Error ? error.message : String(error ?? "");
  const normalized = `${code} ${message}`.toLowerCase();

  if (normalized.includes("resource-exhausted") || normalized.includes("too-many-requests") || normalized.includes("locked")) {
    return copy.pinLockedError;
  }

  if (normalized.includes("firebase web config")) {
    return copy.firebaseMissing;
  }

  if (normalized.includes("internal") || normalized.includes("unavailable")) {
    return copy.subscriptionError;
  }

  return copy.phoneSigninError;
}

function getInvoiceFilename(invoice: AccountInvoiceView, locale: WebLocale) {
  const reference =
    invoice.invoiceNumber ?? invoice.paymentReference ?? invoice.id;
  const documentName =
    locale === "mg" ? "faktiora" : locale === "en" ? "invoice" : "facture";
  return `kajikajy-${documentName}-${reference.replace(/[^\w.-]/g, "_")}.html`;
}

async function buildInvoiceHtml(
  copy: AccountCopy,
  locale: WebLocale,
  invoice: AccountInvoiceView,
  customer: AccountCustomerView | null,
  subscription: SubscriptionView,
) {
  const [{ default: invoiceRubikFont }, { default: invoiceHeadingFont }] =
    await Promise.all([
      import("@fontsource-variable/rubik/files/rubik-latin-wght-normal.woff2?inline"),
      import("@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?inline"),
    ]);
  const invoiceDate = formatDate(
    invoice.paidAt ?? invoice.createdAt,
    locale,
    copy.emptyValue,
  );
  const planLabel = getPlanLabel(invoice.planId, copy.emptyValue);
  const statusLabel = copy.invoiceStatuses[invoice.status];
  const accountLabel =
    customer?.businessName ??
    customer?.displayName ??
    formatStoredPhoneNumber(subscription.phoneNumber) ??
    customer?.email ??
    copy.emptyValue;
  const invoiceNumber =
    invoice.invoiceNumber ?? invoice.paymentReference ?? invoice.id;

  const rows = [
    [copy.invoiceNumberLabel, invoiceNumber],
    [copy.invoiceDateLabel, invoiceDate],
    [copy.activePlanLabel, planLabel],
    [copy.statusLabel, statusLabel],
    [copy.invoiceAmountLabel, formatMoney(invoice.amountMGA)],
    [copy.paymentReferenceLabel, invoice.paymentReference ?? copy.emptyValue],
    [copy.accountInfoTitle, accountLabel],
  ];

  return `<!doctype html>
<html lang="${locale}">
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(copy.invoiceDocumentTitle)} ${escapeHtml(invoiceNumber)}</title>
  <style>
    @font-face { font-family: "Rubik Variable"; font-style: normal; font-weight: 300 900; src: url("${invoiceRubikFont}") format("woff2"); }
    @font-face { font-family: "Inter Variable"; font-style: normal; font-weight: 100 900; src: url("${invoiceHeadingFont}") format("woff2"); }
    body { margin: 0; background: #f7f7f7; color: #101015; font-family: "Rubik Variable", sans-serif; }
    main { width: min(760px, calc(100% - 48px)); margin: 40px auto; background: #fff; padding: 38px; }
    header { display: flex; justify-content: space-between; gap: 24px; border-bottom: 1px solid #ddd; padding-bottom: 24px; }
    h1 { margin: 0; font-size: 28px; font-family: "Inter Variable", sans-serif; letter-spacing: -0.02em; }
    .brand { margin: 0 0 10px; font-family: Arial, sans-serif; font-weight: 800; color: #ff2b2b; }
    .issuer { margin-top: 18px; color: #555; font-size: 12px; line-height: 1.55; }
    .issuer strong { display: block; color: #101015; font-size: 13px; }
    dl { display: grid; grid-template-columns: 1fr 1fr; margin: 28px 0 0; }
    div { min-height: 70px; border-bottom: 1px solid #e5e5e5; padding: 14px 16px 14px 0; }
    dt { color: #666; font-size: 12px; font-weight: 800; text-transform: uppercase; }
    dd { margin: 7px 0 0; font-size: 16px; font-weight: 800; }
    footer { margin-top: 28px; color: #777; font-size: 12px; }
    @media print { body { background: #fff; } main { width: auto; margin: 0; padding: 0; } }
  </style>
</head>
<body>
  <main>
    <header>
      <section>
        <p class="brand">Kajikajy</p>
        <h1>${escapeHtml(copy.invoiceDocumentTitle)}</h1>
        <address class="issuer">
          <strong>${escapeHtml(invoiceIssuer.name)}</strong>
          NIF : ${escapeHtml(invoiceIssuer.nif)}<br>
          RCS : ${escapeHtml(invoiceIssuer.rcs)}<br>
          STAT : ${escapeHtml(invoiceIssuer.stat)}<br>
          ${escapeHtml(invoiceIssuer.address)}
        </address>
      </section>
      <strong>${escapeHtml(formatMoney(invoice.amountMGA))}</strong>
    </header>
    <dl>
      ${rows
        .map(
          ([label, value]) =>
            `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`,
        )
        .join("")}
    </dl>
    <footer>${escapeHtml(WEB_I18N[locale].site.copyright)}</footer>
  </main>
</body>
</html>`;
}

async function downloadInvoice(
  copy: AccountCopy,
  locale: WebLocale,
  invoice: AccountInvoiceView,
  customer: AccountCustomerView | null,
  subscription: SubscriptionView,
) {
  const html = await buildInvoiceHtml(copy, locale, invoice, customer, subscription);
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = getInvoiceFilename(invoice, locale);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

type DetailRowProps = {
  label: string;
  value: string;
};

function DetailRow({ label, value }: DetailRowProps) {
  return (
    <div className="detail-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

type SubscriptionPanelProps = {
  copy: AccountCopy;
  locale: WebLocale;
  subscription: SubscriptionView;
};

function SubscriptionPanel({
  copy,
  locale,
  subscription,
}: SubscriptionPanelProps) {
  const planLabel = getPlanLabel(subscription.planId, copy.emptyValue);
  const statusLabel = getStatusLabel(subscription.status, copy);
  const expiresAt = formatDate(subscription.expiresAt, locale, copy.emptyValue);

  return (
    <section
      className="subscription-panel subscription-panel--current"
      aria-label={copy.statusGridLabel}
    >
      <div className="subscription-head">
        <div>
          <p className="panel-kicker">{copy.subscriptionTitle}</p>
          <h2>{planLabel}</h2>
          <p>
            {statusLabel} · {copy.expiresLabel} {expiresAt}
          </p>
        </div>
      </div>

      <div className="details-grid">
        <DetailRow
          label={copy.graceLabel}
          value={formatDate(subscription.graceUntil, locale, copy.emptyValue)}
        />
        <DetailRow
          label={copy.activatedLabel}
          value={formatDate(subscription.activatedAt, locale, copy.emptyValue)}
        />
        <DetailRow
          label={copy.invoiceLabel}
          value={subscription.invoiceId ?? copy.emptyValue}
        />
        <DetailRow
          label={copy.paymentReferenceLabel}
          value={subscription.paymentReference ?? copy.emptyValue}
        />
        <DetailRow
          label={copy.phoneLabel}
          value={
            formatStoredPhoneNumber(subscription.phoneNumber) ?? copy.emptyValue
          }
        />
      </div>
    </section>
  );
}

type InvoicesPanelProps = {
  customer: AccountCustomerView | null;
  copy: AccountCopy;
  invoices: readonly AccountInvoiceView[];
  locale: WebLocale;
  subscription: SubscriptionView;
};

function InvoicesPanel({
  copy,
  customer,
  invoices,
  locale,
  subscription,
}: InvoicesPanelProps) {
  return (
    <section className="subscription-panel invoices-panel">
      <div className="section-title">
        <FileText aria-hidden size={22} />
        <h2>{copy.invoicesTitle}</h2>
      </div>
      {invoices.length === 0 ? (
        <div className="empty-block">
          <strong>{copy.invoicesEmptyTitle}</strong>
          <p>{copy.invoicesEmptyText}</p>
        </div>
      ) : (
        <div className="invoice-list">
          {invoices.map((invoice) => (
            <article className="invoice-row" key={invoice.id}>
              <div>
                <strong>{getPlanLabel(invoice.planId, copy.emptyValue)}</strong>
                <span>
                  {invoice.invoiceNumber ??
                    invoice.paymentReference ??
                    invoice.id}
                </span>
              </div>
              <div className="invoice-amount">
                <strong>{formatMoney(invoice.amountMGA)}</strong>
                <span>{copy.invoiceStatuses[invoice.status]}</span>
              </div>
              <time>
                {formatDate(
                  invoice.paidAt ?? invoice.createdAt,
                  locale,
                  copy.emptyValue,
                )}
              </time>
              <button
                className="invoice-download"
                type="button"
                aria-label={`${copy.invoiceDownloadLabel} ${invoice.invoiceNumber ?? invoice.paymentReference ?? invoice.id}`}
                title={copy.invoiceDownloadLabel}
                onClick={() =>
                  downloadInvoice(copy, locale, invoice, customer, subscription)
                }
              >
                <Download aria-hidden size={16} />
                <span className="invoice-download-label">{copy.invoiceDownloadLabel}</span>
              </button>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

type AccountInfoPanelProps = {
  copy: AccountCopy;
  customer: AccountCustomerView | null;
  subscription: SubscriptionView;
  user: User | null;
};

function AccountInfoPanel({
  copy,
  customer,
  subscription,
  user,
}: AccountInfoPanelProps) {
  return (
    <section className="subscription-panel account-info-panel">
      <div className="section-title">
        <UserRound aria-hidden size={22} />
        <h2>{copy.accountInfoTitle}</h2>
      </div>
      <div className="details-grid">
        <DetailRow
          label={copy.businessLabel}
          value={customer?.businessName ?? copy.emptyValue}
        />
        <DetailRow
          label={copy.phoneLabel}
          value={
            formatStoredPhoneNumber(subscription.phoneNumber) ??
            formatStoredPhoneNumber(user?.phoneNumber) ??
            copy.emptyValue
          }
        />
        <DetailRow
          label={copy.emailInfoLabel}
          value={customer?.email ?? user?.email ?? copy.emptyValue}
        />
      </div>
    </section>
  );
}

function FooterSocialLinks() {
  return (
    <div className="footer-social-links">
      <a
        aria-label="LinkedIn"
        className="footer-social-link"
        href={WEB_SOCIAL_LINKS.linkedin}
        rel="noopener noreferrer"
        target="_blank"
      >
        <svg
          aria-hidden="true"
          focusable="false"
          viewBox={WEB_SOCIAL_ICONS.linkedin.viewBox}
        >
          <path fill="currentColor" d={WEB_SOCIAL_ICONS.linkedin.path} />
        </svg>
      </a>
      <a
        aria-label="Facebook"
        className="footer-social-link"
        href={WEB_SOCIAL_LINKS.facebook}
        rel="noopener noreferrer"
        target="_blank"
      >
        <svg
          aria-hidden="true"
          focusable="false"
          viewBox={WEB_SOCIAL_ICONS.facebook.viewBox}
        >
          <path fill="currentColor" d={WEB_SOCIAL_ICONS.facebook.path} />
        </svg>
      </a>
    </div>
  );
}

type RenewalPanelProps = {
  accountInvoiceError: string;
  accountInvoiceLoading: AccountInvoiceAction | null;
  actionsText: string;
  canUpgrade: boolean;
  className: string;
  copy: AccountCopy;
  onInvoiceAction: (
    action: AccountInvoiceAction,
    planId: KajikajyPlanId,
  ) => void;
  renewalPlanId: KajikajyPlanId;
};

function RenewalPanel({
  accountInvoiceError,
  accountInvoiceLoading,
  actionsText,
  canUpgrade,
  className,
  copy,
  onInvoiceAction,
  renewalPlanId,
}: RenewalPanelProps) {
  return (
    <aside className={`actions-panel ${className}`}>
      <h2>{copy.actionsTitle}</h2>
      <p>{actionsText}</p>

      <div className="actions-list">
        <button
          className="kj-button kj-button--primary kj-button--block"
          disabled={accountInvoiceLoading !== null}
          type="button"
          onClick={() => onInvoiceAction("renewal", renewalPlanId)}
        >
          <RefreshCw aria-hidden size={18} />
          {accountInvoiceLoading === "renewal"
            ? copy.accountInvoicePreparing
            : copy.renewButton}
        </button>
        {canUpgrade ? (
          <button
            className="kj-button kj-button--secondary kj-button--block"
            disabled={accountInvoiceLoading !== null}
            type="button"
            onClick={() => onInvoiceAction("upgrade", "excellence")}
          >
            <ArrowUpRight aria-hidden size={18} />
            {accountInvoiceLoading === "upgrade"
              ? copy.accountInvoicePreparing
              : copy.upgradeButton}
          </button>
        ) : null}
      </div>

      {accountInvoiceError ? (
        <p className="account-invoice-error">{accountInvoiceError}</p>
      ) : null}
    </aside>
  );
}

type PaymentDialogProps = {
  action: AccountInvoiceAction | null;
  checkoutUrl: string | null;
  copy: AccountCopy;
  error: string;
  flowCopy: (typeof WEB_I18N)[WebLocale]["subscribe"];
  flowPhase: PaymentFlowPhase;
  loading: boolean;
  onClose: () => void;
  onContinueSameTab: () => void;
  onPay: () => void;
  onRetry: () => void;
  onVerify: () => void;
  payment: CreateAccountPaymentSessionResult | null;
  sameTabAvailable: boolean;
};

function PaymentDialog({
  action,
  checkoutUrl,
  copy,
  error,
  flowCopy,
  flowPhase,
  loading,
  onClose,
  onContinueSameTab,
  onPay,
  onRetry,
  onVerify,
  payment,
  sameTabAvailable,
}: PaymentDialogProps) {
  const statusCopy =
    flowPhase === "popup_open"
      ? {
          title: flowCopy.paymentOpeningTitle,
          body: flowCopy.paymentOpeningText,
          tone: "waiting",
        }
      : flowPhase === "verifying"
        ? {
            title: flowCopy.paymentVerifyingTitle,
            body: flowCopy.paymentVerifyingText,
            tone: "waiting",
          }
        : flowPhase === "interrupted"
          ? {
              title: flowCopy.paymentInterruptedTitle,
              body: flowCopy.paymentInterruptedText,
              tone: "interrupted",
            }
          : flowPhase === "delayed"
            ? {
                title: flowCopy.paymentDelayedTitle,
                body: flowCopy.paymentDelayedText,
                tone: "waiting",
              }
            : flowPhase === "succeeded"
              ? {
                  title: flowCopy.paymentSuccessTitle,
                  body: flowCopy.paymentSuccessText,
                  tone: "success",
                }
              : flowPhase === "failed"
                ? {
                    title: flowCopy.paymentFailureTitle,
                    body: flowCopy.paymentFailureText,
                    tone: "failure",
                  }
                : flowPhase === "popup_blocked"
                  ? {
                      title: flowCopy.paymentPopupBlockedTitle,
                      body: flowCopy.paymentPopupBlockedText,
                      tone: "failure",
                    }
                  : null;

  const dialogRef = useRef<HTMLElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(
    typeof document === "undefined"
      ? null
      : (document.activeElement as HTMLElement | null),
  );

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocusedRef.current?.focus();
    };
  }, []);

  useEffect(() => {
    if (flowPhase === "popup_open") {
      return;
    }
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }
    const activeElement = document.activeElement as HTMLElement | null;
    const focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    if (
      activeElement &&
      dialog.contains(activeElement) &&
      activeElement.matches(focusableSelector)
    ) {
      return;
    }
    const firstControl = dialog.querySelector<HTMLElement>(focusableSelector);
    (firstControl ?? dialog).focus();
  }, [checkoutUrl, error, flowPhase, loading, payment, sameTabAvailable]);

  useEffect(() => {
    if (flowPhase !== "popup_open") {
      return undefined;
    }
    const restoreDialogFocus = () => {
      const dialog = dialogRef.current;
      if (!dialog) {
        return;
      }
      const firstControl = dialog.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      (firstControl ?? dialog).focus();
    };
    window.addEventListener("focus", restoreDialogFocus);
    return () => window.removeEventListener("focus", restoreDialogFocus);
  }, [flowPhase]);

  return (
    <div className="payment-modal">
      <div className="payment-modal-backdrop" aria-hidden="true" />
      <section
        ref={dialogRef}
        className="payment-dialog"
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby="payment-dialog-title"
        aria-describedby="payment-dialog-description"
        aria-busy={
          loading || flowPhase === "popup_open" || flowPhase === "verifying"
        }
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            onClose();
            return;
          }
          if (event.key !== "Tab") {
            return;
          }
          const focusable = Array.from(
            event.currentTarget.querySelectorAll<HTMLElement>(
              'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
            ),
          ).filter((element) => element.getClientRects().length > 0);
          if (focusable.length === 0) {
            event.preventDefault();
            return;
          }
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }}
      >
        <button
          autoFocus
          className="payment-dialog-close"
          type="button"
          aria-label={copy.closeButton}
          onClick={onClose}
        >
          <X aria-hidden size={18} />
        </button>

        <div className="payment-dialog-heading">
          <CreditCard aria-hidden size={22} />
          <div>
            <p className="panel-kicker">{copy.paymentDialogTitle}</p>
            <h2 id="payment-dialog-title">
              {statusCopy?.title ??
                (payment
                  ? action === "upgrade"
                    ? copy.accountInvoiceUpgradeReadyText
                    : copy.accountInvoiceRenewalReadyText
                  : copy.accountInvoicePreparing)}
            </h2>
          </div>
        </div>

        {loading ? (
          <div
            className="payment-dialog-loader"
            role="status"
            aria-live="polite"
          >
            <LoaderCircle aria-hidden size={40} />
            <p id="payment-dialog-description">{copy.paymentPreparingText}</p>
          </div>
        ) : statusCopy ? (
          <>
            <div
              className={`payment-dialog-status payment-dialog-status--${statusCopy.tone}`}
              role={flowPhase === "failed" ? "alert" : "status"}
              aria-live={flowPhase === "failed" ? "assertive" : "polite"}
            >
              {statusCopy.tone === "waiting" ? (
                <LoaderCircle
                  className="payment-dialog-status-icon payment-dialog-status-icon--spin"
                  aria-hidden
                  size={38}
                />
              ) : statusCopy.tone === "success" ? (
                <CheckCircle2
                  className="payment-dialog-status-icon"
                  aria-hidden
                  size={38}
                />
              ) : (
                <X
                  className="payment-dialog-status-icon"
                  aria-hidden
                  size={38}
                />
              )}
              <p id="payment-dialog-description">{statusCopy.body}</p>
            </div>
            <div className="payment-dialog-actions">
              {flowPhase === "popup_blocked" ? (
                <>
                  <button
                    className="kj-button kj-button--primary kj-button--block"
                    type="button"
                    onClick={onPay}
                  >
                    {flowCopy.reopenPaymentButton}
                  </button>
                  <button
                    className="kj-button kj-button--secondary kj-button--block"
                    type="button"
                    disabled={!sameTabAvailable}
                    onClick={onContinueSameTab}
                  >
                    {flowCopy.continueSameTabButton}
                  </button>
                  {!sameTabAvailable ? (
                    <p className="payment-dialog-error" role="alert">
                      {flowCopy.paymentStorageUnavailableText}
                    </p>
                  ) : null}
                </>
              ) : null}
              {flowPhase === "interrupted" ? (
                <>
                  <button
                    className="kj-button kj-button--primary kj-button--block"
                    type="button"
                    onClick={onVerify}
                  >
                    {flowCopy.checkAgainButton}
                  </button>
                  <button
                    className="kj-button kj-button--secondary kj-button--block"
                    type="button"
                    onClick={onPay}
                  >
                    {flowCopy.reopenPaymentButton}
                  </button>
                </>
              ) : null}
              {flowPhase === "delayed" ? (
                <>
                  <button
                    className="kj-button kj-button--primary kj-button--block"
                    type="button"
                    onClick={onVerify}
                  >
                    {flowCopy.checkAgainButton}
                  </button>
                  {checkoutUrl ? (
                    <button
                      className="kj-button kj-button--secondary kj-button--block"
                      type="button"
                      onClick={onPay}
                    >
                      {flowCopy.reopenPaymentButton}
                    </button>
                  ) : null}
                </>
              ) : null}
              {flowPhase === "failed" ? (
                <button
                  className="kj-button kj-button--primary kj-button--block"
                  type="button"
                  onClick={onRetry}
                >
                  {copy.retryButton}
                </button>
              ) : null}
              <button
                className="kj-button kj-button--secondary kj-button--block"
                type="button"
                onClick={onClose}
              >
                {copy.closeButton}
              </button>
            </div>
          </>
        ) : payment ? (
          <>
            <p id="payment-dialog-description">{copy.paymentDialogText}</p>

            <dl className="payment-dialog-details">
              <div>
                <dt>{copy.accountInvoiceReferenceLabel}</dt>
                <dd>{payment.paymentReference}</dd>
              </div>
              <div>
                <dt>{copy.accountInvoiceAmountLabel}</dt>
                <dd>{formatMoney(payment.amountMGA)}</dd>
              </div>
              <div>
                <dt>{copy.paymentProviderLabel}</dt>
                <dd>{payment.providerLabel}</dd>
              </div>
            </dl>

            {!payment.checkoutUrl ? (
              <p className="payment-dialog-error">
                {copy.paymentUnavailableText}
              </p>
            ) : null}

            <div className="payment-dialog-actions">
              <button
                className="kj-button kj-button--primary kj-button--block"
                type="button"
                disabled={!payment.checkoutUrl}
                onClick={onPay}
              >
                {copy.payButton}
              </button>
              <button
                className="kj-button kj-button--secondary kj-button--block"
                type="button"
                onClick={onClose}
              >
                {copy.closeButton}
              </button>
            </div>
          </>
        ) : (
          <>
            <p
              className="payment-dialog-error"
              id="payment-dialog-description"
              role="alert"
            >
              {error || copy.accountInvoiceError}
            </p>
            <div className="payment-dialog-actions">
              <button
                className="kj-button kj-button--primary kj-button--block"
                type="button"
                onClick={onRetry}
              >
                {copy.retryButton}
              </button>
              <button
                className="kj-button kj-button--secondary kj-button--block"
                type="button"
                onClick={onClose}
              >
                {copy.closeButton}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function isLocalAccountPreviewEnabled() {
  if (!import.meta.env.DEV || typeof window === "undefined") {
    return false;
  }

  if (!["localhost", "127.0.0.1", "::1"].includes(window.location.hostname)) {
    return false;
  }

  return (
    new URLSearchParams(window.location.search).get("preview") === "account"
  );
}

function createLocalAccountPreview(): AccountDashboardView {
  const params = new URLSearchParams(window.location.search);
  const excellence = params.get("plan") === "excellence";
  const requestedStatus = params.get("status");
  const status = requestedStatus === "grace" || requestedStatus === "expired"
    ? requestedStatus
    : "active";
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;
  return {
    customer: null,
    invoices: [],
    subscription: {
      activatedAt: excellence ? new Date(now - 30 * day).toISOString() : null,
      expiresAt: excellence
        ? new Date(now + (status === "active" ? 30 : -1) * day).toISOString()
        : null,
      graceUntil: excellence
        ? new Date(now + (status === "expired" ? -1 : status === "active" ? 37 : 6) * day).toISOString()
        : null,
      invoiceId: null,
      paymentProvider: null,
      paymentReference: null,
      phoneNumber: null,
      planId: excellence ? "excellence" : "essor",
      status,
      uid: "local-preview",
    },
  };
}

function isLegacyPinActivationPath(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  return window.location.pathname.replace(/\/+$/, "") === "/activer-pin";
}

export function App() {
  return isLegacyPinActivationPath() ? (
    <LegacyPinActivationPage />
  ) : (
    <AccountApp />
  );
}

function LegacyPinActivationPage() {
  const { theme, toggleTheme } = useThemeMode();
  const { locale, setLocale } = useWebLocale();
  const common = WEB_I18N[locale].common;
  const accountCopy = ACCOUNT_COPY[locale];
  const copy = LEGACY_PIN_ACTIVATION_COPY[locale];
  const [phoneCountryCode, setPhoneCountryCode] = useState(
    DEFAULT_PHONE_COUNTRY_CODE,
  );
  const [localPhoneNumber, setLocalPhoneNumber] = useState("");
  const [activationToken, setActivationToken] = useState("");
  const [pin, setPin] = useState("");
  const [pinConfirmation, setPinConfirmation] = useState("");
  const [activationError, setActivationError] = useState("");
  const [activating, setActivating] = useState(false);
  const [activated, setActivated] = useState(false);

  const fullPhoneNumber = useMemo(
    () => buildE164PhoneNumber(phoneCountryCode, localPhoneNumber),
    [localPhoneNumber, phoneCountryCode],
  );
  const phoneIsValid = useMemo(
    () => isValidLocalPhone(phoneCountryCode, localPhoneNumber),
    [localPhoneNumber, phoneCountryCode],
  );

  useEffect(() => {
    document.title = `${copy.title} | Kajikajy`;
  }, [copy.title]);

  useEffect(() => {
    const fragment = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : "";
    const parameters = new URLSearchParams(fragment);
    const token = parameters.get("token")?.trim() ?? "";

    if (token) {
      setActivationToken(token);
    }

    if (parameters.has("token")) {
      window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${window.location.search}`,
      );
    }
  }, []);

  async function handleActivation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (activating) {
      return;
    }

    setActivationError("");

    if (!phoneIsValid) {
      setActivationError(accountCopy.phoneValidationError);
      return;
    }

    const normalizedActivationToken = activationToken.trim();
    if (!normalizedActivationToken) {
      setActivationError(copy.activationCodeRequired);
      return;
    }

    if (!/^\d{4}$/.test(pin)) {
      setActivationError(copy.pinRequired);
      return;
    }

    if (pin !== pinConfirmation) {
      setActivationError(copy.pinMismatch);
      return;
    }

    setActivating(true);
    try {
      const result = await activateLegacyPhonePin({
        phoneNumber: fullPhoneNumber,
        activationToken: normalizedActivationToken,
        pin,
        pinConfirmation,
      });
      if (!result.ok) {
        setActivationError(copy.activationError);
        return;
      }

      setActivationToken("");
      setPin("");
      setPinConfirmation("");
      setActivated(true);
    } catch {
      setActivationError(copy.activationError);
    } finally {
      setActivating(false);
    }
  }

  return (
    <main className="kj-shell page kj-page-atmosphere">
      <header className="kj-topbar">
        <div className="kj-brand" aria-label="Kajikajy">
          <img src="/kajikajy.png" alt="" />
          <span>Kajikajy</span>
        </div>
        <div className="kj-topbar-actions">
          <LanguageMenu
            label={common.language}
            locale={locale}
            onLocaleChange={setLocale}
          />
          <button
            className="kj-theme-toggle"
            type="button"
            aria-label={theme === "dark" ? common.lightMode : common.darkMode}
            onClick={toggleTheme}
          >
            {theme === "dark" ? (
              <Sun aria-hidden size={18} />
            ) : (
              <ThemeMoonIcon />
            )}
          </button>
        </div>
      </header>

      <section className="account-hero kj-page-atmosphere__opening">
        <div className="intro">
          <p className="eyebrow">{copy.eyebrow}</p>
          <h1>{copy.title}</h1>
          <p className="lead">{copy.lead}</p>
        </div>

        {activated ? (
          <section className="signin-panel" aria-live="polite">
            <div className="panel-heading">
              <h2>{copy.successTitle}</h2>
              <p>{copy.successText}</p>
            </div>
          </section>
        ) : (
          <form className="signin-panel" onSubmit={handleActivation}>
            <div className="panel-heading">
              <h2>{copy.formTitle}</h2>
              <p>{copy.formText}</p>
            </div>

            <PhoneCountryField
              label={accountCopy.phoneNumberLabel}
              countryLabel={accountCopy.phoneCountryLabel}
              searchPlaceholder={accountCopy.phoneCountrySearch}
              noResults={accountCopy.phoneCountryNoResults}
              placeholder={accountCopy.phonePlaceholder}
              countryCode={phoneCountryCode}
              localNumber={localPhoneNumber}
              onCountryCodeChange={setPhoneCountryCode}
              onLocalNumberChange={setLocalPhoneNumber}
            />

            <label className="field">
              <span>{copy.activationCodeLabel}</span>
              <input
                autoCapitalize="none"
                autoComplete="one-time-code"
                name="activation-token"
                placeholder={copy.activationCodePlaceholder}
                required
                spellCheck={false}
                type="text"
                value={activationToken}
                onChange={(event) => setActivationToken(event.target.value)}
              />
            </label>

            <label className="field pin-field">
              <span>{copy.createPinLabel}</span>
              <input
                autoComplete="new-password"
                inputMode="numeric"
                maxLength={4}
                name="new-account-pin"
                pattern="[0-9]{4}"
                placeholder={accountCopy.pinPlaceholder}
                required
                type="password"
                value={pin}
                onChange={(event) =>
                  setPin(event.target.value.replace(/\D/g, "").slice(0, 4))
                }
              />
            </label>

            <label className="field pin-field">
              <span>{copy.confirmPinLabel}</span>
              <input
                autoComplete="new-password"
                inputMode="numeric"
                maxLength={4}
                name="new-account-pin-confirmation"
                pattern="[0-9]{4}"
                placeholder={accountCopy.pinPlaceholder}
                required
                type="password"
                value={pinConfirmation}
                onChange={(event) =>
                  setPinConfirmation(
                    event.target.value.replace(/\D/g, "").slice(0, 4),
                  )
                }
              />
            </label>

            {activationError ? (
              <p className="form-error" role="alert">
                {activationError}
              </p>
            ) : null}

            <button
              className="kj-button kj-button--primary kj-button--block"
              disabled={activating}
              type="submit"
            >
              {activating ? (
                <LoaderCircle
                  className="auth-submit-spinner"
                  aria-hidden
                  size={18}
                />
              ) : (
                <CheckCircle2 aria-hidden size={18} />
              )}
              {activating ? copy.activatingButton : copy.activateButton}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}

function WebAppAccessLink({
  subscription,
  userUid,
  copy,
}: {
  subscription: SubscriptionView;
  userUid: string | null;
  copy: AccountCopy;
}) {
  const accessUntil = getWebAppAccessUntil(subscription);
  const [now, setNow] = useState(Date.now);
  const [opening, setOpening] = useState(false);
  const [failure, setFailure] = useState<"popup-blocked" | "failed" | null>(null);
  const handoffRef = useRef<WebSessionHandoff | null>(null);

  useEffect(() => {
    setOpening(false);
    setFailure(null);
    return () => {
      const active = handoffRef.current;
      handoffRef.current = null;
      active?.cancel();
    };
  }, [userUid, accessUntil]);

  useEffect(() => {
    let timer: number | undefined;
    const refresh = () => {
      const currentTime = Date.now();
      setNow(currentTime);
      if (timer !== undefined) window.clearTimeout(timer);
      if (accessUntil !== null && accessUntil >= currentTime) {
        timer = window.setTimeout(
          refresh,
          Math.min(accessUntil - currentTime + 1, 2_147_483_647),
        );
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    refresh();
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [accessUntil]);

  if (accessUntil === null || now > accessUntil) return null;

  function openWebApp() {
    if (!userUid || handoffRef.current) return;
    setFailure(null);
    setOpening(true);
    const handoff = startWebSessionHandoff({
      host: window,
      begin: (challenge) => beginWebAppSession(challenge, userUid),
      isCurrentSession: () => isWebSessionAccountCurrent(userUid),
    });
    handoffRef.current = handoff;
    void handoff.result.catch((error: unknown) => {
      if (handoffRef.current !== handoff) return;
      if (error instanceof WebSessionHandoffError && error.reason === "cancelled") return;
      setFailure(error instanceof WebSessionHandoffError && error.reason === "popup-blocked"
        ? "popup-blocked" : "failed");
    }).finally(() => {
      if (handoffRef.current !== handoff) return;
      handoffRef.current = null;
      setOpening(false);
    });
  }

  return (
    <>
      <button
        className="kj-button account-web-access"
        type="button"
        onClick={openWebApp}
        disabled={opening || !userUid}
        aria-busy={opening}
      >
        {opening ? <LoaderCircle className="auth-submit-spinner" aria-hidden size={18} /> : null}
        {copy.openKajikajy}
      </button>
      {failure ? <p className="form-error" role="alert">
        {failure === "popup-blocked" ? copy.webAccessPopupBlocked : copy.webAccessError}
      </p> : null}
    </>
  );
}

function AccountApp() {
  const { theme, toggleTheme } = useThemeMode();
  const { locale, setLocale } = useWebLocale();
  const common = WEB_I18N[locale].common;
  const copy = ACCOUNT_COPY[locale];
  const accountPreviewEnabled = useMemo(
    () => isLocalAccountPreviewEnabled(),
    [],
  );
  const [user, setUser] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(accountPreviewEnabled);
  const [signinMode, setSigninMode] = useState<SigninMode>("phone");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState(
    DEFAULT_PHONE_COUNTRY_CODE,
  );
  const [localPhoneNumber, setLocalPhoneNumber] = useState("");
  const [phonePin, setPhonePin] = useState("");
  const [authError, setAuthError] = useState("");
  const [showAccountLookupHelp, setShowAccountLookupHelp] = useState(false);
  const [dashboard, setDashboard] = useState<AccountDashboardView | null>(
    accountPreviewEnabled ? createLocalAccountPreview() : null,
  );
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [accountPayment, setAccountPayment] =
    useState<CreateAccountPaymentSessionResult | null>(null);
  const [accountInvoiceAction, setAccountInvoiceAction] =
    useState<AccountInvoiceAction | null>(null);
  const [accountInvoiceLoading, setAccountInvoiceLoading] =
    useState<AccountInvoiceAction | null>(null);
  const [accountInvoiceError, setAccountInvoiceError] = useState("");
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentFlowPhase, setPaymentFlowPhase] =
    useState<PaymentFlowPhase>("ready");
  const [pendingPayment, setPendingPayment] = useState<PendingPayment | null>(
    null,
  );
  const [paymentPersistenceAvailable, setPaymentPersistenceAvailable] =
    useState(true);
  const [verificationMode, setVerificationMode] =
    useState<PaymentVerificationMode | null>(null);
  const [verificationNonce, setVerificationNonce] = useState(0);
  const paymentPopupRef = useRef<Window | null>(null);
  const popupMonitorRef = useRef<number | null>(null);
  const verificationStartedAtRef = useRef(0);
  const singleCheckFallbackPhaseRef = useRef<
    Extract<PaymentFlowPhase, "interrupted" | "delayed">
  >("interrupted");
  const accountPaymentRequestIdRef = useRef(0);
  const accountPaymentLockRef = useRef<"creating" | "active" | null>(null);
  const paymentDialogDismissedWhileCreatingRef = useRef(false);
  const latestUserRef = useRef<User | null>(user);
  latestUserRef.current = user;

  const fullPhoneNumber = useMemo(
    () => buildE164PhoneNumber(phoneCountryCode, localPhoneNumber),
    [localPhoneNumber, phoneCountryCode],
  );
  const phoneIsValid = useMemo(
    () => isValidLocalPhone(phoneCountryCode, localPhoneNumber),
    [localPhoneNumber, phoneCountryCode],
  );

  const pinIsValid = /^\d{4}$/.test(phonePin);
  function stopPopupMonitor() {
    if (popupMonitorRef.current !== null) {
      window.clearInterval(popupMonitorRef.current);
      popupMonitorRef.current = null;
    }
  }

  function closeTrackedPaymentPopup() {
    stopPopupMonitor();
    const popup = paymentPopupRef.current;
    paymentPopupRef.current = null;
    if (popup && !popup.closed) {
      popup.close();
    }
  }

  function persistDismissedPendingPayment() {
    if (!pendingPayment) {
      return;
    }
    const dismissedPayment: PendingPayment = {
      ...pendingPayment,
      dismissedAt: new Date().toISOString(),
    };
    setPaymentPersistenceAvailable(
      persistPendingPayment(window.sessionStorage, dismissedPayment),
    );
    setPendingPayment(dismissedPayment);
  }

  function clearPendingPaymentDismissal(): PendingPayment | null {
    if (!pendingPayment) {
      return null;
    }
    if (!pendingPayment.dismissedAt) {
      return pendingPayment;
    }
    const activePayment: PendingPayment = {
      ...pendingPayment,
      dismissedAt: undefined,
    };
    setPaymentPersistenceAvailable(
      persistPendingPayment(window.sessionStorage, activePayment),
    );
    setPendingPayment(activePayment);
    return activePayment;
  }

  function startInterruptedVerification() {
    singleCheckFallbackPhaseRef.current = "interrupted";
    if (!verificationStartedAtRef.current) {
      verificationStartedAtRef.current = Date.now();
    }
    setPaymentFlowPhase((current) =>
      current === "succeeded" || current === "failed"
        ? current
        : "interrupted",
    );
    setVerificationMode("once");
    setVerificationNonce((current) => current + 1);
  }

  function closePaymentDialog() {
    const hadOpenPopup = Boolean(paymentPopupRef.current);
    const shouldCheckAfterClosing =
      hadOpenPopup ||
      paymentFlowPhase === "popup_open" ||
      (paymentFlowPhase === "verifying" &&
        verificationMode === "continuous");
    closeTrackedPaymentPopup();
    if (
      !pendingPayment &&
      (accountPaymentLockRef.current === "creating" ||
        accountInvoiceLoading !== null)
    ) {
      paymentDialogDismissedWhileCreatingRef.current = true;
    }
    if (
      pendingPayment &&
      paymentFlowPhase !== "succeeded" &&
      paymentFlowPhase !== "failed"
    ) {
      persistDismissedPendingPayment();
    }
    if (
      shouldCheckAfterClosing &&
      paymentFlowPhase !== "succeeded" &&
      paymentFlowPhase !== "failed"
    ) {
      startInterruptedVerification();
    }
    setPaymentDialogOpen(false);
  }

  function resetAccountPaymentTracking() {
    accountPaymentRequestIdRef.current += 1;
    closeTrackedPaymentPopup();
    clearPendingPayment(window.sessionStorage, "account");
    accountPaymentLockRef.current = null;
    paymentDialogDismissedWhileCreatingRef.current = false;
    setPendingPayment(null);
    setPaymentPersistenceAvailable(true);
    setAccountPayment(null);
    setAccountInvoiceLoading(null);
    setPaymentFlowPhase("ready");
    setVerificationMode(null);
    verificationStartedAtRef.current = 0;
  }

  async function refreshAccountAfterConfirmedPayment() {
    if (!latestUserRef.current) {
      return;
    }

    try {
      const result = await getAccountDashboard();
      if (result.account) {
        setDashboard(result.account);
      }
    } catch {
      // The success state remains valid even if refreshing the dashboard fails.
    }
  }

  useEffect(() => {
    document.title = `${copy.title} | Kajikajy`;
  }, [copy.title]);

  useEffect(() => {
    const returnedPayment = parsePaymentReturnQuery(window.location.search);
    if (returnedPayment) {
      window.history.replaceState(
        {},
        "",
        removePaymentReturnQuery(new URL(window.location.href)),
      );
    }

    const savedPayment = readPendingPayment(window.sessionStorage, "account");
    if (
      savedPayment &&
      (!returnedPayment?.paymentIntentId ||
        returnedPayment.paymentIntentId === savedPayment.paymentIntentId)
    ) {
      const restoredPayment: PendingPayment = returnedPayment
        ? { ...savedPayment, dismissedAt: undefined }
        : savedPayment;
      if (returnedPayment && savedPayment.dismissedAt) {
        setPaymentPersistenceAvailable(
          persistPendingPayment(window.sessionStorage, restoredPayment),
        );
      } else {
        setPaymentPersistenceAvailable(true);
      }
      accountPaymentLockRef.current = "active";
      setPendingPayment(restoredPayment);

      if (returnedPayment?.status === "success") {
        setPaymentFlowPhase("verifying");
        setVerificationMode("continuous");
        verificationStartedAtRef.current = Date.now();
        setPaymentDialogOpen(true);
      } else if (returnedPayment?.status === "failure") {
        singleCheckFallbackPhaseRef.current = "interrupted";
        setPaymentFlowPhase("interrupted");
        setVerificationMode("once");
        verificationStartedAtRef.current = Date.now();
        setPaymentDialogOpen(true);
      } else if (restoredPayment.dismissedAt) {
        setPaymentFlowPhase("interrupted");
        setVerificationMode(null);
        setPaymentDialogOpen(false);
      } else {
        singleCheckFallbackPhaseRef.current = "interrupted";
        setPaymentFlowPhase("interrupted");
        setVerificationMode("once");
        verificationStartedAtRef.current = Date.now();
        setPaymentDialogOpen(true);
      }
    }
  }, []);

  useEffect(() => {
    const handlePaymentMessage = (event: MessageEvent) => {
      const popup = paymentPopupRef.current;
      if (!popup || event.source !== popup) {
        return;
      }

      const isPapiMessage = isPapiPaymentStatusMessage(
        event,
        PAPI_PAYMENT_ORIGIN,
      );
      const isKajikajyReturn =
        isAllowedKajikajyPaymentOrigin(event.origin) &&
        isKajikajyPaymentReturnMessage(event);
      if (!isPapiMessage && !isKajikajyReturn) {
        return;
      }

      if (isKajikajyReturn) {
        closeTrackedPaymentPopup();
      }
      if (isKajikajyReturn && event.data.status === "failure") {
        startInterruptedVerification();
      } else {
        setPaymentFlowPhase("verifying");
        setVerificationMode("continuous");
        setVerificationNonce((current) => current + 1);
        if (!verificationStartedAtRef.current) {
          verificationStartedAtRef.current = Date.now();
        }
      }
    };

    window.addEventListener("message", handlePaymentMessage);
    return () => {
      window.removeEventListener("message", handlePaymentMessage);
      closeTrackedPaymentPopup();
    };
  }, []);

  useEffect(() => {
    if (
      !verificationMode ||
      !pendingPayment?.paymentIntentId ||
      !pendingPayment.statusToken
    ) {
      return undefined;
    }

    let disposed = false;
    let finished = false;
    let requestInFlight = false;
    let timer: number | null = null;
    let attempts = 0;
    const paymentExpiresAt = Date.parse(pendingPayment.expiresAt);

    const confirmationIsDelayed = () => {
      const startedAt = verificationStartedAtRef.current;
      return startedAt > 0 && Date.now() - startedAt >= 90_000;
    };

    const paymentHasExpired = () =>
      Number.isFinite(paymentExpiresAt) && Date.now() >= paymentExpiresAt;

    const schedule = (delay: number) => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
      if (!disposed && !finished) {
        timer = window.setTimeout(() => void verify(), delay);
      }
    };

    const verify = async () => {
      if (disposed || finished || requestInFlight) {
        return;
      }
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
      if (document.hidden) {
        schedule(1000);
        return;
      }
      if (paymentHasExpired()) {
        finished = true;
        setVerificationMode(null);
        setPaymentFlowPhase((current) =>
          current === "succeeded" || current === "failed"
            ? current
            : verificationMode === "once"
              ? singleCheckFallbackPhaseRef.current
              : "delayed",
        );
        return;
      }

      requestInFlight = true;
      let stopPolling = false;
      try {
        const result = await getPaymentStatus({
          paymentIntentId: pendingPayment.paymentIntentId!,
          paymentReference: pendingPayment.paymentReference,
          statusToken: pendingPayment.statusToken!,
        });
        if (disposed) {
          return;
        }

        const nextPhase = mapInvoiceStatusToPaymentPhase(result.status);
        if (nextPhase === "succeeded" || nextPhase === "failed") {
          stopPolling = true;
          finished = true;
          closeTrackedPaymentPopup();
          clearPendingPayment(window.sessionStorage, "account");
          accountPaymentLockRef.current = null;
          setPendingPayment(null);
          setPaymentPersistenceAvailable(true);
          setAccountPayment(null);
          setVerificationMode(null);
          setPaymentFlowPhase(nextPhase);
          if (nextPhase === "succeeded") {
            void refreshAccountAfterConfirmedPayment();
          }
        } else if (verificationMode === "once") {
          stopPolling = true;
          finished = true;
          setVerificationMode(null);
          setPaymentFlowPhase((current) =>
            current === "succeeded" || current === "failed"
              ? current
              : singleCheckFallbackPhaseRef.current,
          );
        } else if (confirmationIsDelayed()) {
          setPaymentFlowPhase("delayed");
        } else {
          setPaymentFlowPhase((current) =>
            current === "popup_open" || current === "interrupted"
              ? current
              : "verifying",
          );
        }
      } catch {
        if (!disposed && verificationMode === "once") {
          stopPolling = true;
          finished = true;
          setVerificationMode(null);
          setPaymentFlowPhase((current) =>
            current === "succeeded" || current === "failed"
              ? current
              : singleCheckFallbackPhaseRef.current,
          );
        } else if (!disposed && confirmationIsDelayed()) {
          setPaymentFlowPhase("delayed");
        }
      } finally {
        requestInFlight = false;
        if (!disposed && !finished && !stopPolling) {
          attempts += 1;
          schedule(
            confirmationIsDelayed()
              ? 15_000
              : attempts === 1
                ? 1500
                : attempts === 2
                  ? 3000
                  : 5000,
          );
        }
      }
    };

    const verifyWhenVisible = () => {
      if (!document.hidden) {
        void verify();
      }
    };
    window.addEventListener("focus", verifyWhenVisible);
    window.addEventListener("pageshow", verifyWhenVisible);
    document.addEventListener("visibilitychange", verifyWhenVisible);
    void verify();

    return () => {
      disposed = true;
      if (timer !== null) {
        window.clearTimeout(timer);
      }
      window.removeEventListener("focus", verifyWhenVisible);
      window.removeEventListener("pageshow", verifyWhenVisible);
      document.removeEventListener("visibilitychange", verifyWhenVisible);
    };
  }, [pendingPayment, verificationMode, verificationNonce]);

  function openPaymentPopup() {
    const checkoutUrl =
      accountPayment?.checkoutUrl ?? pendingPayment?.checkoutUrl ?? null;
    const paymentIntentId =
      accountPayment?.paymentIntentId ?? pendingPayment?.paymentIntentId;
    if (!checkoutUrl || !paymentIntentId) {
      return;
    }

    clearPendingPaymentDismissal();
    closeTrackedPaymentPopup();
    const popup = openCenteredPaymentPopup(
      checkoutUrl,
      `papi-payment-${paymentIntentId}`,
    );
    if (!popup) {
      setPaymentFlowPhase("popup_blocked");
      setVerificationMode(null);
      return;
    }

    paymentPopupRef.current = popup;
    verificationStartedAtRef.current = 0;
    setPaymentFlowPhase("popup_open");
    setVerificationMode("continuous");
    setVerificationNonce((current) => current + 1);
    popup.focus();

    popupMonitorRef.current = window.setInterval(() => {
      if (!popup.closed) {
        return;
      }
      stopPopupMonitor();
      if (paymentPopupRef.current === popup) {
        paymentPopupRef.current = null;
      }
      startInterruptedVerification();
    }, 500);
  }

  function continuePaymentInCurrentTab() {
    const checkoutUrl =
      accountPayment?.checkoutUrl ?? pendingPayment?.checkoutUrl ?? null;
    if (!checkoutUrl || !pendingPayment) {
      setPaymentPersistenceAvailable(false);
      return;
    }
    const activePayment: PendingPayment = {
      ...pendingPayment,
      dismissedAt: undefined,
    };
    if (!persistPendingPayment(window.sessionStorage, activePayment)) {
      setPaymentPersistenceAvailable(false);
      return;
    }
    setPendingPayment(activePayment);
    setPaymentPersistenceAvailable(true);
    closeTrackedPaymentPopup();
    setVerificationMode(null);
    navigateToPaymentCheckout(checkoutUrl);
  }

  function verifyPaymentNow() {
    if (!pendingPayment) {
      return;
    }
    verificationStartedAtRef.current = Date.now();
    singleCheckFallbackPhaseRef.current =
      paymentFlowPhase === "delayed" ? "delayed" : "interrupted";
    setPaymentFlowPhase("verifying");
    setVerificationMode("once");
    setVerificationNonce((current) => current + 1);
  }

  useEffect(() => {
    if (accountPreviewEnabled) {
      return undefined;
    }

    try {
      return observeAccountUser((nextUser) => {
        setUser(nextUser);
        setAuthReady(true);
      });
    } catch (error) {
      setAuthReady(true);
      setAuthError(
        error instanceof Error ? error.message : copy.firebaseMissing,
      );
      return undefined;
    }
  }, [accountPreviewEnabled, copy.firebaseMissing]);

  useEffect(() => {
    let cancelled = false;

    if (accountPreviewEnabled) {
      return () => {
        cancelled = true;
      };
    }

    if (!authReady || !user) {
      return () => {
        cancelled = true;
      };
    }

    setDashboardLoading(true);
    setAuthError("");

    getAccountDashboard()
      .then(async (result) => {
        if (cancelled) {
          return;
        }
        if (!result.account) {
          setDashboard(null);
          await signOutAccount();
          if (!cancelled) {
            setAuthError(copy.noPaidAccountText);
            setShowAccountLookupHelp(true);
          }
          return;
        }
        setDashboard(result.account);
        setShowAccountLookupHelp(false);
      })
      .catch(async (error) => {
        if (cancelled) {
          return;
        }

        setDashboard(null);
        if (user.isAnonymous && isPermissionDeniedError(error)) {
          setAuthError(copy.reconnectRequired);
          setShowAccountLookupHelp(false);
          setDashboardLoading(false);
          await signOutAccount().catch(() => undefined);
          return;
        }

        setAuthError(getAccountErrorMessage(error, copy));
        setShowAccountLookupHelp(true);
      })
      .finally(() => {
        if (!cancelled) {
          setDashboardLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    accountPreviewEnabled,
    authReady,
    copy.noPaidAccountText,
    copy.reconnectRequired,
    copy.subscriptionError,
    user,
  ]);

  async function handleSignIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    resetAccountPaymentTracking();
    setAuthError("");
    setShowAccountLookupHelp(false);
    setDashboard(null);
    setAccountPayment(null);
    setAccountInvoiceError("");
    setPaymentDialogOpen(false);

    try {
      await signInAccount(email, password);
      setPassword("");
    } catch {
      setAuthError(copy.signinError);
      setShowAccountLookupHelp(true);
    }
  }

  async function handlePhoneSignIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    resetAccountPaymentTracking();
    setAuthError("");
    setShowAccountLookupHelp(false);
    setDashboard(null);
    setAccountPayment(null);
    setAccountInvoiceError("");
    setPaymentDialogOpen(false);

    if (!phoneIsValid) {
      setAuthError(copy.phoneValidationError);
      return;
    }

    if (!pinIsValid) {
      setAuthError(copy.pinValidationError);
      return;
    }

    setDashboardLoading(true);
    try {
      await signInAccountWithPhonePin(fullPhoneNumber, phonePin);
      const result = await getAccountDashboard();
      if (!result.account) {
        await signOutAccount();
        setAuthError(copy.phoneSigninError);
        return;
      }
      setDashboard(result.account);
      setPhonePin("");
      setShowAccountLookupHelp(false);
    } catch (error) {
      await signOutAccount().catch(() => undefined);
      setAuthError(getPhonePinErrorMessage(error, copy));
      setShowAccountLookupHelp(false);
    } finally {
      setDashboardLoading(false);
    }
  }

  function handleModeChange(mode: SigninMode) {
    resetAccountPaymentTracking();
    setSigninMode(mode);
    setPhonePin("");
    setAuthError("");
    setShowAccountLookupHelp(false);
    setAccountPayment(null);
    setAccountInvoiceError("");
    setPaymentDialogOpen(false);
  }

  async function handleSignOut() {
    resetAccountPaymentTracking();
    setPhonePin("");
    setAuthError("");
    setShowAccountLookupHelp(false);
    setAccountPayment(null);
    setAccountInvoiceError("");
    setPaymentDialogOpen(false);
    if (accountPreviewEnabled) {
      setDashboard(createLocalAccountPreview());
      return;
    }
    setDashboard(null);
    if (user) {
      await signOutAccount();
    }
  }

  const subscription = dashboard?.subscription ?? null;

  async function handleAccountInvoice(
    action: AccountInvoiceAction,
    planId: KajikajyPlanId,
    forceNew = false,
  ) {
    if (!dashboard || !subscription) {
      return;
    }
    if (accountPaymentLockRef.current === "creating") {
      paymentDialogDismissedWhileCreatingRef.current = false;
      setPaymentDialogOpen(true);
      return;
    }
    if (
      (accountPaymentLockRef.current === "active" ||
        accountPayment ||
        pendingPayment) &&
      !forceNew
    ) {
      setPaymentDialogOpen(true);
      return;
    }

    if (forceNew) {
      resetAccountPaymentTracking();
    }

    accountPaymentLockRef.current = "creating";
    paymentDialogDismissedWhileCreatingRef.current = false;
    const requestId = accountPaymentRequestIdRef.current + 1;
    accountPaymentRequestIdRef.current = requestId;
    const requestIsCurrent = () =>
      accountPaymentRequestIdRef.current === requestId;

    setAccountInvoiceLoading(action);
    setAccountPayment(null);
    setAccountInvoiceAction(action);
    setAccountInvoiceError("");
    setPaymentDialogOpen(true);

    try {
      const payment = await createAccountPaymentSession({
        intent: action,
        planId,
        phoneNumber: subscription.phoneNumber,
      });
      if (!requestIsCurrent()) {
        return;
      }
      const dismissedWhileCreating =
        paymentDialogDismissedWhileCreatingRef.current;
      paymentDialogDismissedWhileCreatingRef.current = false;
      const nextPendingPayment: PendingPayment = {
        version: 1,
        source: "account",
        invoiceId: payment.invoiceId,
        paymentIntentId: payment.paymentIntentId,
        paymentReference: payment.paymentReference,
        statusToken: payment.statusToken,
        checkoutUrl: payment.checkoutUrl ?? undefined,
        accountPhoneNumber: subscription.phoneNumber ?? undefined,
        expiresAt: payment.expiresAt,
        createdAt: new Date().toISOString(),
        dismissedAt: dismissedWhileCreating
          ? new Date().toISOString()
          : undefined,
      };
      accountPaymentLockRef.current = "active";
      setPaymentPersistenceAvailable(
        persistPendingPayment(window.sessionStorage, nextPendingPayment),
      );
      setPendingPayment(nextPendingPayment);
      setPaymentFlowPhase(dismissedWhileCreating ? "interrupted" : "ready");
      setVerificationMode(null);
      verificationStartedAtRef.current = 0;
      setAccountPayment(payment);
      if (dismissedWhileCreating) {
        setPaymentDialogOpen(false);
      }
    } catch {
      if (requestIsCurrent()) {
        accountPaymentLockRef.current = null;
        paymentDialogDismissedWhileCreatingRef.current = false;
        setAccountInvoiceError(copy.accountInvoiceError);
      }
    } finally {
      if (requestIsCurrent()) {
        setAccountInvoiceLoading(null);
      }
    }
  }

  const subscribeHomeHref = getSubscribeHomeHref();
  const legalText = WEB_I18N[locale].site;
  const canUpgrade = subscription?.planId !== "excellence";
  const actionsText = canUpgrade
    ? copy.actionsUpgradeText
    : copy.actionsRenewText;
  const renewalPlanId = subscription?.planId ?? "essor";

  function handleHeaderRenewal() {
    const planId = dashboard?.subscription.planId;
    if (!planId) {
      return;
    }
    void handleAccountInvoice("renewal", planId);
  }

  return (
    <main className="kj-shell page kj-page-atmosphere">
      <header className="kj-topbar">
        <a className="kj-brand" href={getSiteHref("/")} aria-label="Kajikajy">
          <img src="/kajikajy.png" alt="" />
          <span>Kajikajy</span>
        </a>
        <div className="kj-topbar-actions">
          {dashboard && subscription?.planId ? (
            <button
              aria-busy={accountInvoiceLoading === "renewal"}
              className="kj-nav-link kj-nav-link--primary account-renew-nav"
              disabled={accountInvoiceLoading !== null}
              type="button"
              onClick={handleHeaderRenewal}
            >
              {copy.renewButton}
            </button>
          ) : (
            <a
              className="kj-nav-link kj-nav-link--primary"
              href={subscribeHomeHref}
            >
              {common.subscribe}
            </a>
          )}
          <LanguageMenu
            label={common.language}
            locale={locale}
            onLocaleChange={setLocale}
          />
          <button
            className="kj-theme-toggle"
            type="button"
            aria-label={theme === "dark" ? common.lightMode : common.darkMode}
            onClick={toggleTheme}
          >
            {theme === "dark" ? (
              <Sun aria-hidden size={18} />
            ) : (
              <ThemeMoonIcon />
            )}
          </button>
        </div>
      </header>

      <section className="account-hero kj-page-atmosphere__opening">
        <div className="intro">
          <h1>{copy.title}</h1>
          <p className="lead">{copy.lead}</p>
        </div>

        {!dashboard ? (
          <form
            className="signin-panel"
            onSubmit={signinMode === "phone" ? handlePhoneSignIn : handleSignIn}
          >
            <div className="panel-heading">
              <h2>{copy.signinTitle}</h2>
              <p>{copy.signinText}</p>
            </div>

            <div
              className="signin-switch"
              role="tablist"
              aria-label={copy.signinTitle}
            >
              <button
                className={
                  signinMode === "phone"
                    ? "signin-tab signin-tab--active"
                    : "signin-tab"
                }
                type="button"
                role="tab"
                aria-selected={signinMode === "phone"}
                onClick={() => handleModeChange("phone")}
              >
                <Phone aria-hidden size={16} />
                {copy.phoneMode}
              </button>
              <button
                className={
                  signinMode === "email"
                    ? "signin-tab signin-tab--active"
                    : "signin-tab"
                }
                type="button"
                role="tab"
                aria-selected={signinMode === "email"}
                onClick={() => handleModeChange("email")}
              >
                <Mail aria-hidden size={16} />
                {copy.emailMode}
              </button>
            </div>

            {signinMode === "phone" ? (
              <>
                <PhoneCountryField
                  label={copy.phoneNumberLabel}
                  countryLabel={copy.phoneCountryLabel}
                  searchPlaceholder={copy.phoneCountrySearch}
                  noResults={copy.phoneCountryNoResults}
                  placeholder={copy.phonePlaceholder}
                  countryCode={phoneCountryCode}
                  localNumber={localPhoneNumber}
                  onCountryCodeChange={setPhoneCountryCode}
                  onLocalNumberChange={setLocalPhoneNumber}
                />

                <label className="field pin-field">
                  <span>{copy.pinLabel}</span>
                  <input
                    aria-describedby="phone-pin-support"
                    autoComplete="current-password"
                    inputMode="numeric"
                    maxLength={4}
                    name="account-pin"
                    pattern="[0-9]{4}"
                    placeholder={copy.pinPlaceholder}
                    required
                    type="password"
                    value={phonePin}
                    onChange={(event) =>
                      setPhonePin(
                        event.target.value.replace(/\D/g, "").slice(0, 4),
                      )
                    }
                  />
                </label>

                <p className="pin-support-help" id="phone-pin-support">
                  <span>{copy.forgotPinPrompt}</span>
                  <span>{copy.forgotPinAction}</span>
                </p>
              </>
            ) : (
              <>
                <label className="field">
                  <span>{copy.emailLabel}</span>
                  <input
                    autoComplete="email"
                    inputMode="email"
                    name="email"
                    placeholder={copy.emailPlaceholder}
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </label>

                <label className="field">
                  <span>{copy.passwordLabel}</span>
                  <input
                    autoComplete="current-password"
                    name="password"
                    placeholder={copy.passwordPlaceholder}
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </label>
              </>
            )}

            {authError ? <p className="form-error">{authError}</p> : null}

            <button
              className="kj-button kj-button--primary kj-button--block"
              disabled={dashboardLoading}
              type="submit"
            >
              {dashboardLoading ? (
                <LoaderCircle
                  className="auth-submit-spinner"
                  aria-hidden
                  size={18}
                />
              ) : (
                <LogIn aria-hidden size={18} />
              )}
              {dashboardLoading ? copy.loading : copy.signinButton}
            </button>

            <div
              className={
                showAccountLookupHelp
                  ? "account-lookup-help account-lookup-help--active"
                  : "account-lookup-help"
              }
            >
              <p>
                {showAccountLookupHelp
                  ? copy.accountLookupHelpText
                  : copy.accountLookupHelpTitle}
              </p>
              <a href={subscribeHomeHref}>{copy.accountLookupHelpButton}</a>
            </div>
          </form>
        ) : (
          <div className="account-summary-stack">
            <aside className="account-summary">
              <p>{copy.signedInAs}</p>
              <strong>
                {formatStoredPhoneNumber(dashboard.subscription.phoneNumber) ??
                  dashboard.customer?.email ??
                  user?.email ??
                  copy.emptyValue}
              </strong>
              <button
                className="text-button"
                type="button"
                onClick={handleSignOut}
              >
                <LogOut aria-hidden size={17} />
                {copy.signOut}
              </button>
            </aside>
            <WebAppAccessLink
              subscription={dashboard.subscription}
              userUid={user?.uid ?? null}
              copy={copy}
            />
          </div>
        )}
      </section>

      {dashboard && subscription ? (
        <section className="account-grid">
          <div className="main-stack">
            <SubscriptionPanel
              copy={copy}
              locale={locale}
              subscription={subscription}
            />
            <RenewalPanel
              accountInvoiceError={accountInvoiceError}
              accountInvoiceLoading={accountInvoiceLoading}
              actionsText={actionsText}
              canUpgrade={canUpgrade}
              className="actions-panel--phone"
              copy={copy}
              onInvoiceAction={handleAccountInvoice}
              renewalPlanId={renewalPlanId}
            />
            <AccountInfoPanel
              copy={copy}
              customer={dashboard.customer}
              subscription={subscription}
              user={user}
            />
            <InvoicesPanel
              copy={copy}
              customer={dashboard.customer}
              invoices={dashboard.invoices}
              locale={locale}
              subscription={subscription}
            />
          </div>

          <RenewalPanel
            accountInvoiceError={accountInvoiceError}
            accountInvoiceLoading={accountInvoiceLoading}
            actionsText={actionsText}
            canUpgrade={canUpgrade}
            className="actions-panel--wide"
            copy={copy}
            onInvoiceAction={handleAccountInvoice}
            renewalPlanId={renewalPlanId}
          />
        </section>
      ) : null}

      {paymentDialogOpen ? (
        <PaymentDialog
          action={accountInvoiceAction}
          checkoutUrl={
            accountPayment?.checkoutUrl ?? pendingPayment?.checkoutUrl ?? null
          }
          copy={copy}
          error={accountInvoiceError}
          flowCopy={WEB_I18N[locale].subscribe}
          flowPhase={paymentFlowPhase}
          loading={accountInvoiceLoading !== null}
          onClose={closePaymentDialog}
          onContinueSameTab={continuePaymentInCurrentTab}
          onPay={openPaymentPopup}
          onRetry={() => {
            if (!accountInvoiceAction) {
              return;
            }
            void handleAccountInvoice(
              accountInvoiceAction,
              accountInvoiceAction === "upgrade" ? "excellence" : renewalPlanId,
              true,
            );
          }}
          onVerify={verifyPaymentNow}
          payment={accountPayment}
          sameTabAvailable={paymentPersistenceAvailable}
        />
      ) : null}

      <footer className="portal-footer">
        <nav aria-label={WEB_I18N[locale].subscribe.legalLinksLabel}>
          <a href={getSiteHref(WEB_SITE_PATHS[locale].about)}>{legalText.about}</a>
          <a href={getSiteHref("/confidentialite")}>{legalText.legalPrivacy}</a>
          <a href={getSiteHref("/confidentialite-app")}>{legalText.legalAppPrivacy}</a>
          <a href={getSiteHref("/conditions")}>{legalText.legalTerms}</a>
          <a href={getSiteHref("/mentions-legales")}>{legalText.legalNotice}</a>
          <a href={getSiteHref("/suppression-de-compte")}>{legalText.legalDeletion}</a>
        </nav>
        <div className="portal-footer-copy">
          <FooterSocialLinks />
          <span>{legalText.copyright}</span>
        </div>
      </footer>
    </main>
  );
}
